import i18next from 'i18next';
import moment from 'moment-timezone';

import {getWeekdayTitle} from 'web-app/react/entities/plans/helpers';
import type {DayOfTheWeek, MonthlyInvoicingOptions} from '@famly/vet_api-types';
import {getDayOfTheMonth} from 'web-app/util/moment';

import {MonthlyInvoicingDue, WeeklyInvoicingDue} from './types';

export const getOrNull = <T>(value: T | undefined | null): T | null => value ?? null;

export const invoicePeriodData = {
    NEXT: {
        label: i18next.t('automaticInvoicing.configuration.nextPeriod'),
        examples: {
            weekly: i18next.t('automaticInvoicing.configuration.invoicingPeriodExamples.nextPeriod.weekly'),
            biweekly: i18next.t('automaticInvoicing.configuration.invoicingPeriodExamples.nextPeriod.biweekly'),
            monthly: i18next.t('automaticInvoicing.configuration.invoicingPeriodExamples.nextPeriod.monthly'),
        },
    },
    CURRENT: {
        label: i18next.t('automaticInvoicing.configuration.currentPeriod'),
        examples: {
            weekly: i18next.t('automaticInvoicing.configuration.invoicingPeriodExamples.currentPeriod.weekly'),
            biweekly: i18next.t('automaticInvoicing.configuration.invoicingPeriodExamples.currentPeriod.biweekly'),
            monthly: i18next.t('automaticInvoicing.configuration.invoicingPeriodExamples.currentPeriod.monthly'),
        },
    },
    PREVIOUS: {
        label: i18next.t('automaticInvoicing.configuration.previousPeriod'),
        examples: {
            weekly: i18next.t('automaticInvoicing.configuration.invoicingPeriodExamples.previousPeriod.weekly'),
            biweekly: i18next.t('automaticInvoicing.configuration.invoicingPeriodExamples.previousPeriod.biweekly'),
            monthly: i18next.t('automaticInvoicing.configuration.invoicingPeriodExamples.previousPeriod.monthly'),
        },
    },
};

export const getBiWeeklyWeekdayTitle = (dayOfWeek: DayOfTheWeek) => {
    const weekDay = getWeekdayTitle(dayOfWeek);
    return i18next.t('react.routes.account.institution.automaticInvoicingConfig.translations.json.everyOtherWeekday', {
        weekDay,
    });
};

export const getBiWeeklyDueDateWeekdayTitle = (dayOfWeek: DayOfTheWeek) => {
    const weekDay = getWeekdayTitle(dayOfWeek);
    return i18next.t('react.routes.account.institution.automaticInvoicingConfig.translations.json.followingWeekday', {
        weekDay,
    });
};

export const weekdayToJsDay: Record<DayOfTheWeek, number> = {
    ['SUNDAY']: 0,
    ['MONDAY']: 1,
    ['TUESDAY']: 2,
    ['WEDNESDAY']: 3,
    ['THURSDAY']: 4,
    ['FRIDAY']: 5,
    ['SATURDAY']: 6,
};

/**
 * Given a DayOfTheWeek (e.g., 'MONDAY'), returns a Moment object
 * representing the next *upcoming* occurrence of that weekday.
 * If today is the same weekday, the result will be for the following week.
 *
 * @param dayOfWeek - Enum value from DayOfTheWeek (e.g., 'THURSDAY')
 * @returns Moment representing the upcoming weekday
 */
export const upcomingOccurrence = (dayOfWeek: DayOfTheWeek): moment.Moment => {
    const targetDay = weekdayToJsDay[dayOfWeek];
    const today = moment();
    const todayDay = today.day();

    let daysUntilTarget = (targetDay - todayDay + 7) % 7;
    if (daysUntilTarget === 0) {
        daysUntilTarget = 7;
    }

    return today.clone().add(daysUntilTarget, 'days');
};

/**
 * Given a Moment object, returns the corresponding DayOfTheWeek enum value
 * (e.g., 'MONDAY', 'FRIDAY') based on the day of the week.
 *
 * @param moment - A Moment instance
 * @returns A value from the DayOfTheWeek enum representing the weekday
 */
export const dayOfWeekFromMoment = (moment: moment.Moment | null): DayOfTheWeek | null => {
    if (!moment) {
        return null;
    }
    const jsDay = moment.day();
    return (Object.keys(weekdayToJsDay) as DayOfTheWeek[]).find(key => weekdayToJsDay[key] === jsDay) ?? null;
};

export const daysOfTheMonthOptions = () => {
    return Array.from({length: 28}, (_, index) => index + 1).map(day => ({
        value: day.toString(),
        label: getDayOfTheMonth(day),
    }));
};

export const monthlyBillingOptions = (): {
    value: MonthlyInvoicingOptions;
    label: string;
}[] => {
    return [
        {
            value: 'FIRST_WEEK_DAY_OF_MONTH',
            label: i18next.t('settings.payments.automaticPayments.form.firstWeekDayOption'),
        },
        {
            value: 'LAST_WEEK_DAY_OF_MONTH',
            label: i18next.t('settings.payments.automaticPayments.form.lastOfMonthOption'),
        },
        {
            value: 'EXACT_DAY_OF_MONTH',
            label: i18next.t('settings.payments.automaticPayments.form.exactDayOption'),
        },
    ];
};

export function monthlyInvoiceDueOptions(selectedBillingOption?: string) {
    const optionToDueMap: Record<string, typeof MonthlyInvoicingDue[keyof typeof MonthlyInvoicingDue]> = {
        FIRST_WEEK_DAY_OF_MONTH: MonthlyInvoicingDue.FIRST_WORKING_DAY_OF_MONTH,
        LAST_WEEK_DAY_OF_MONTH: MonthlyInvoicingDue.LAST_WORKING_DAY_OF_MONTH,
        EXACT_DAY_OF_MONTH: MonthlyInvoicingDue.EXACT_DAY_OF_MONTH,
    };

    const dueToExclude = selectedBillingOption ? optionToDueMap[selectedBillingOption] : undefined;

    return [
        {
            value: MonthlyInvoicingDue.FIRST_WORKING_DAY_OF_MONTH,
            label: i18next.t('settings.payments.automaticPayments.form.firstWeekDayOption'),
            description: i18next.t(
                'react.routes.account.institution.automaticInvoicingConfig.translations.json.followingMonth',
            ),
        },
        {
            value: MonthlyInvoicingDue.LAST_WORKING_DAY_OF_MONTH,
            label: i18next.t('settings.payments.automaticPayments.form.lastOfMonthOption'),
        },
        {
            value: MonthlyInvoicingDue.EXACT_DAY_OF_MONTH,
            label: i18next.t('settings.payments.automaticPayments.form.exactDayOption'),
        },
        {
            value: MonthlyInvoicingDue.RELATIVE_TO_BILLING_DATE,
            label: i18next.t(
                'react.routes.account.institution.automaticInvoicingConfig.translations.json.relativeToBillingDate',
            ),
        },
    ].filter(option => option.value !== dueToExclude);
}
export const biWeeklyBillingOptions = () => {
    return [
        {value: 'MONDAY', label: getBiWeeklyWeekdayTitle('MONDAY')},
        {value: 'TUESDAY', label: getBiWeeklyWeekdayTitle('TUESDAY')},
        {value: 'WEDNESDAY', label: getBiWeeklyWeekdayTitle('WEDNESDAY')},
        {value: 'THURSDAY', label: getBiWeeklyWeekdayTitle('THURSDAY')},
        {value: 'FRIDAY', label: getBiWeeklyWeekdayTitle('FRIDAY')},
        {value: 'SATURDAY', label: getBiWeeklyWeekdayTitle('SATURDAY')},
        {value: 'SUNDAY', label: getBiWeeklyWeekdayTitle('SUNDAY')},
    ];
};

export const biWeeklyInvoiceDueOptions = () => {
    return [
        {value: 'MONDAY', label: getBiWeeklyDueDateWeekdayTitle('MONDAY')},
        {value: 'TUESDAY', label: getBiWeeklyDueDateWeekdayTitle('TUESDAY')},
        {value: 'WEDNESDAY', label: getBiWeeklyDueDateWeekdayTitle('WEDNESDAY')},
        {value: 'THURSDAY', label: getBiWeeklyDueDateWeekdayTitle('THURSDAY')},
        {value: 'FRIDAY', label: getBiWeeklyDueDateWeekdayTitle('FRIDAY')},
        {value: 'SATURDAY', label: getBiWeeklyDueDateWeekdayTitle('SATURDAY')},
        {value: 'SUNDAY', label: getBiWeeklyDueDateWeekdayTitle('SUNDAY')},
        {
            value: WeeklyInvoicingDue.RELATIVE_TO_BILLING_DATE,
            label: i18next.t(
                'react.routes.account.institution.automaticInvoicingConfig.translations.json.relativeToBillingDate',
            ),
        },
    ];
};

export const weeklyBillingOptions = () => {
    return [
        {value: 'MONDAY', label: getWeekdayTitle('MONDAY')},
        {value: 'TUESDAY', label: getWeekdayTitle('TUESDAY')},
        {value: 'WEDNESDAY', label: getWeekdayTitle('WEDNESDAY')},
        {value: 'THURSDAY', label: getWeekdayTitle('THURSDAY')},
        {value: 'FRIDAY', label: getWeekdayTitle('FRIDAY')},
        {value: 'SATURDAY', label: getWeekdayTitle('SATURDAY')},
        {value: 'SUNDAY', label: getWeekdayTitle('SUNDAY')},
    ];
};

export const weeklyInvoiceDueOptions = (selectedBillingOption?: string) => {
    return [
        ...weeklyBillingOptions().filter(option => option.value !== selectedBillingOption),
        {
            value: WeeklyInvoicingDue.RELATIVE_TO_BILLING_DATE,
            label: i18next.t(
                'react.routes.account.institution.automaticInvoicingConfig.translations.json.relativeToBillingDate',
            ),
        },
    ];
};

export function weeklyInvoicingDueInput(invoicingDue: string, invoicingDueRelativeToBillingDate: number | undefined) {
    if (!invoicingDue) {
        return undefined;
    }

    if (invoicingDue === WeeklyInvoicingDue.RELATIVE_TO_BILLING_DATE) {
        if (invoicingDueRelativeToBillingDate === undefined) {
            return undefined;
        }

        return {
            type: WeeklyInvoicingDue.RELATIVE_TO_BILLING_DATE,
            daysAfterSendingInvoice: invoicingDueRelativeToBillingDate,
        };
    }

    return {
        type: WeeklyInvoicingDue.EXACT_DAY_OF_WEEK,
        exactDayOfWeek: invoicingDue,
    };
}

export function monthlyInvoicingDueInput(
    invoicingDueType: string | null,
    exactDate: string | null,
    daysAfterBilling: string | null,
) {
    if (!invoicingDueType) {
        return undefined;
    }

    switch (invoicingDueType) {
        case MonthlyInvoicingDue.EXACT_DAY_OF_MONTH:
            if (exactDate) {
                return {
                    type: invoicingDueType,
                    exactDayOfMonth: Number(exactDate),
                };
            }
            return undefined;
        case MonthlyInvoicingDue.RELATIVE_TO_BILLING_DATE:
            if (daysAfterBilling) {
                return {
                    type: invoicingDueType,
                    daysAfterSendingInvoice: Number(daysAfterBilling),
                };
            }
            return undefined;
        case MonthlyInvoicingDue.FIRST_WORKING_DAY_OF_MONTH:
        case MonthlyInvoicingDue.LAST_WORKING_DAY_OF_MONTH:
            return {
                type: invoicingDueType,
            };
        default:
            return undefined;
    }
}
